import React from "react";
import { Link } from "react-router-dom";
import "./CapabilityGrid.css";

/**
 * A titled list of capabilities, each rendered as a full-width row: text on one
 * side, supporting image on the other, alternating down the page so the eye has
 * something to follow.
 *
 * Shared by the AI section on /aiml and the manufacturing section on
 * /cadautomation. One component means the two cannot drift apart the way the
 * seven copy-pasted home page sections did.
 *
 * Item shape: { id, title, lead, body, outcome?, platforms?, img?, webp?, alt? }
 * Items without an image render as a centred text block rather than an empty
 * column — several deck images could not be published, and the layout has to
 * cope with that rather than leaving a hole.
 */
function CapabilityGrid({
  id,
  heading,
  intro,
  items,
  developing,
  developingHeading = "Currently in development",
  cta,
  headingLevel: Heading = "h2",
  variant = "",
}) {
  const headingId = `${id}-heading`;
  const ItemHeading = Heading === "h2" ? "h3" : "h4";

  const renderRow = (item, index, isWip = false) => {
    const hasImage = Boolean(item.img);
    return (
      <article
        className={`cap-row ${index % 2 === 1 ? "cap-row--mirrored" : ""} ${
          hasImage ? "" : "cap-row--textonly"
        } ${isWip ? "cap-row--wip" : ""}`}
        key={item.id}
      >
        <div className="cap-row__text">
          <ItemHeading className="cap-row__title">{item.title}</ItemHeading>
          <p className="cap-row__lead">{item.lead}</p>
          <p className="cap-row__body">{item.body}</p>

          {item.platforms && (
            <ul className="cap-row__tags" aria-label="Applies to">
              {item.platforms.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          )}

          {item.outcome && <p className="cap-row__outcome">{item.outcome}</p>}
        </div>

        {hasImage && (
          <div className="cap-row__media">
            <picture>
              {item.webp && <source srcSet={item.webp} type="image/webp" />}
              <img
                src={item.img}
                alt={item.alt}
                className="cap-row__img"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>
        )}
      </article>
    );
  };

  return (
    <section
      className={`caps ${variant ? `caps--${variant}` : ""}`}
      aria-labelledby={headingId}
    >
      <div className="container">
        <Heading className="caps__heading" id={headingId}>
          {heading}
        </Heading>
        {intro && <p className="caps__intro">{intro}</p>}

        <div className="caps__rows">
          {items.map((item, index) => renderRow(item, index))}
        </div>

        {developing?.length > 0 && (
          <div className="caps__wip">
            <ItemHeading className="caps__wip-heading">
              {developingHeading}
            </ItemHeading>
            <div className="caps__rows">
              {developing.map((item, index) =>
                renderRow(item, items.length + index, true)
              )}
            </div>
          </div>
        )}

        {cta && (
          <div className="caps__cta">
            <p className="caps__cta-text">{cta.text}</p>
            <Link to={cta.to} className="caps__cta-button">
              {cta.label}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default CapabilityGrid;
